import { FormEvent, useEffect, useRef, useState } from "react"
import { t } from "../../i18n"
import { SageCostCode, SageJobSite } from "../../Interfaces"
import { SageApiError } from "../../utils/sageApiError"
import { displayJobCode, isSageCostCode, parseSageCostCode } from "../../utils/sageCodes"
import { sageJobSiteService } from "../../services/sageJobSiteService"
import CwFormModal, { CwField, cwInputClassName } from "./CwFormModal"

export default function JobCodeFormModal({
  isOpen,
  companyId,
  jobCode,
  jobSites,
  lockedJobSiteId,
  onClose,
  onSaved,
}: {
  isOpen: boolean
  companyId: number
  jobCode?: SageCostCode | null
  jobSites: SageJobSite[]
  lockedJobSiteId?: number
  onClose: () => void
  onSaved: () => void
}) {
  const editing = Boolean(jobCode?.id)
  const presetSiteId = lockedJobSiteId || jobCode?.jobSiteId || 0
  const [code, setCode] = useState("")
  const [name, setName] = useState("")
  const [jobSiteId, setJobSiteId] = useState("")
  const [codeError, setCodeError] = useState("")
  const [nameError, setNameError] = useState("")
  const [jobSiteError, setJobSiteError] = useState("")
  const [formError, setFormError] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const codeRef = useRef<HTMLInputElement>(null)
  const lockSite = Boolean(lockedJobSiteId)

  useEffect(() => {
    if (!isOpen) return
    setCode(jobCode ? displayJobCode(jobCode.code) : "")
    setName(jobCode?.name || "")
    setJobSiteId(presetSiteId ? String(presetSiteId) : "")
    setCodeError("")
    setNameError("")
    setJobSiteError("")
    setFormError("")
    setSubmitting(false)
    const id = window.setTimeout(() => codeRef.current?.focus(), 0)
    return () => window.clearTimeout(id)
  }, [isOpen, jobCode, presetSiteId])

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    const trimmedName = name.trim()
    const selectedSiteId = Number(jobSiteId)
    const nextCodeError = !code.trim()
      ? t("jobCodes.codeRequired")
      : isSageCostCode(code)
        ? ""
        : t("jobCodes.codeInvalid")
    const nextNameError = trimmedName ? "" : t("jobCodes.nameRequired")
    const nextSiteError = selectedSiteId ? "" : t("jobCodes.jobSiteRequired")
    setCodeError(nextCodeError)
    setNameError(nextNameError)
    setJobSiteError(nextSiteError)
    setFormError("")
    const parsedCode = parseSageCostCode(code)
    if (nextCodeError || nextNameError || nextSiteError || parsedCode == null) return

    setSubmitting(true)
    try {
      const body = { jobSiteId: selectedSiteId, code: parsedCode, name: trimmedName }
      if (editing && jobCode) {
        await sageJobSiteService.updateCostCode(companyId, jobCode.id, body)
      } else {
        await sageJobSiteService.createCostCode(companyId, body)
      }
      onSaved()
      onClose()
    } catch (error) {
      setFormError(error instanceof SageApiError && error.message
        ? error.message
        : t("jobCodes.loadFailed"))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <CwFormModal
      isOpen={isOpen}
      title={editing ? t("jobCodes.modalEditTitle") : t("jobCodes.modalAddTitle")}
      submitLabel={t("common.save")}
      submitting={submitting}
      formError={formError}
      onClose={onClose}
      onSubmit={handleSubmit}
    >
      <CwField
        id="job-code-value"
        label={t("jobCodes.codeLabel")}
        helper={t("jobCodes.codeHelper")}
        error={codeError}
      >
        <input
          ref={codeRef}
          id="job-code-value"
          name="code"
          inputMode="decimal"
          value={code}
          disabled={submitting}
          onChange={(event) => {
            setCode(event.target.value)
            if (codeError) setCodeError("")
          }}
          className={cwInputClassName}
        />
      </CwField>
      <CwField id="job-code-name" label={t("jobCodes.nameLabel")} error={nameError}>
        <input
          id="job-code-name"
          name="name"
          value={name}
          disabled={submitting}
          onChange={(event) => {
            setName(event.target.value)
            if (nameError) setNameError("")
          }}
          className={cwInputClassName}
        />
      </CwField>
      <CwField id="job-code-site" label={t("jobCodes.jobSiteLabel")} error={jobSiteError}>
        <select
          id="job-code-site"
          name="jobSiteId"
          value={jobSiteId}
          disabled={submitting || lockSite}
          onChange={(event) => {
            setJobSiteId(event.target.value)
            if (jobSiteError) setJobSiteError("")
          }}
          className={cwInputClassName}
        >
          {!lockSite ? <option value="">{t("jobCodes.jobSiteLabel")}</option> : null}
          {jobSites.map((site) => (
            <option key={site.id} value={site.id}>{site.name}</option>
          ))}
        </select>
      </CwField>
    </CwFormModal>
  )
}
